import { checkQuadraticExpression } from "../../../../utils/quadraticExpression";
﻿// src/generators/dzial3_funkcje/temat3_kwadratowa/podtemat1_ogolna_types/TransformGeneralToCanonicalTask.js
import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import EquationInput from "../../../../components/inputs/EquationInput";

/* =============================================================
   TASK: TransformGeneralToCanonicalTask
   Z ogólnej f(x)=ax^2+bx+c zapisz postać kanoniczną f(x)=a(x-p)^2+q.
   Użytkownik wpisuje tylko prawą stronę; UI pokazuje prefiks "f(x) =".
============================================================= */

/* ---------- helpers: liczby i ułamki ---------- */

function randInt(min, max, exclude = new Set()) {
  const span = max - min + 1;
  let v;
  do { v = Math.floor(Math.random() * span) + min; } while (exclude.has(v));
  return v;
}
const par = (v) => (v < 0 ? `(${v})` : `${v}`);

function gcd(a, b){ a=Math.abs(a); b=Math.abs(b); while(b){const t=a%b;a=b;b=t;} return a||1; }
function simplifyFrac(n, d){
  const s = Math.sign(n)*Math.sign(d) || 1;
  const an = Math.abs(n), ad = Math.abs(d);
  const g = gcd(an, ad);
  return { n: s*(an/g), d: ad/g };
}
function formatFractionMixed(n, d){
  const sgn = Math.sign(n) < 0 ? "-" : "";
  const N = Math.abs(n), D = Math.abs(d);
  if (N % D === 0) return `${sgn}${N/D}`;
  const whole = Math.floor(N/D);
  const rem = N % D;
  if (whole === 0) return `${sgn}\\dfrac{${N}}{${D}}`;
  return `${sgn}${whole}\\dfrac{${rem}}{${D}}`;
}

function canonicalLatex(a, pFrac, qFrac) {
  const aLatex = Math.abs(a) === 1 ? (a === -1 ? "-" : "") : String(a);
  const pStr = formatFractionMixed(pFrac.n, pFrac.d);
  const pIsNeg = pFrac.n < 0;
  const pAbsStr = formatFractionMixed(Math.abs(pFrac.n), pFrac.d);
  const qStr = formatFractionMixed(qFrac.n, qFrac.d);
  const qIsNeg = qFrac.n < 0;
  const qAbsStr = formatFractionMixed(Math.abs(qFrac.n), qFrac.d);
  const xMinusP = pIsNeg ? `x + ${pAbsStr}` : `x - ${pStr}`;
  const plusMinusQ = qIsNeg ? ` - ${qAbsStr}` : ` + ${qStr}`;
  return `${aLatex}\\big(${xMinusP}\\big)^{2}${plusMinusQ}`;
}

/* ---------- pobieranie surowego tekstu z EquationInput ---------- */

function getRaw(val) {
  if (typeof val === "string") return val;
  if (val && typeof val === "object") {
    for (const k of ["raw","value","text","input","expr","equation","latex"]) {
      const v = val[k];
      if (typeof v === "string" && v.trim() !== "") return v;
    }
    try {
      const s = String(val);
      if (s && s !== "[object Object]") return s;
    } catch {}
  }
  return "";
}

/* ---------- LaTeX → ascii + ułamki mieszane ---------- */

function latexToAscii(s) {
  return s
    .replace(/\\left\(/g, "(").replace(/\\right\)/g, ")")
    .replace(/\\cdot/g, "")
    .replace(/\^{\s*2\s*}/g, "^2")
    .replace(/\\,/g, "")
    .replace(/\\ /g, "")
    .replace(/\\!/g, "")
    .replace(/\\-/g, "-");
}

// „ - 7 1/5 ” → „-36/5”
// Zamień ułamki mieszane na niewłaściwe,
// NIE gubiąc znaku +/− bezpośrednio przed liczbą mieszaną.
function normalizeMixedFractions(s) {
  return s.replace(
    /([+\-]?)\s*(\d+)\s+(\d+)\s*\/\s*(\d+)/g,
    (_, sign, whole, num, den) => {
      const W = parseInt(whole, 10);
      const N = parseInt(num, 10);
      const D = parseInt(den, 10);
      if (!D) return _; // na wszelki wypadek

      // wartość bez znaku (np. 1 1/6 -> 7/6)
      const improperAbs = W * D + N;

      // zachowujemy oryginalny znak poprzedzający liczbę mieszaną:
      // "+" zostaje "+", "-" zostaje "-", brak znaku = "".
      const signChar = sign === "-" ? "-" : (sign === "+" ? "+" : "");

      return `${signChar}${improperAbs}/${D}`;
    }
  );
}

// liczba: całkowita, zwykły ułamek, dziesiętna (z kropką lub przecinkiem)
function parseNumber(s) {
  if (s == null) return null;
  s = s.trim();
  if (!s) return null;
  s = s.replace(/(\d),(\d)/g, "$1.$2"); // zamień przecinek na kropkę, gdy jest między cyframi
  if (/^[+\-]?\d*\.?\d+(?:e[+\-]?\d+)?$/i.test(s)) return parseFloat(s);
  const m = s.match(/^([+\-]?\d+)\s*\/\s*([+\-]?\d+)$/);
  if (m) {
    const p = parseInt(m[1],10), q = parseInt(m[2],10);
    if (!q) return null;
    return p / q;
  }
  return null;
}

// GŁÓWNA normalizacja do parsowania/eval
function normalizeCanonicalRHS(raw) {
  let s = getRaw(raw);
  if (!s) return "";
  if (s.includes("\\") || /\^{\s*2\s*}/.test(s)) s = latexToAscii(s);
  s = s.replace(/(\d),(\d)/g, "$1.$2");   // 1,25 → 1.25
  s = normalizeMixedFractions(s);        // najpierw mieszane → niewłaściwe
  s = s.replace(/\u2212/g, "-")          // znak „−”
       .replace(/²/g, "^2")
       .replace(/^f\(x\)=/i, "")
       .replace(/^y=/i, "")
       .replace(/\s+/g, "");             // na końcu zbij spacje
  return s;
}

/* ---------- parser formy kanonicznej ---------- */

const NUM = '(?:\\d+\\/\\d+|\\d*\\.\\d+|\\d+)';
export function parseCanonicalUserRaw(raw) {
  const s = normalizeCanonicalRHS(raw);
  if (!s) return null;
  const re = new RegExp(`^([+\\-]?${NUM})?\\(x([+-])(${NUM})\\)\\^2([+-])(${NUM})$`, "i");
  const m = s.match(re);
  if (!m) return null;

  // a
  let aStr = m[1];
  let a;
  if (aStr == null || aStr === "") a = 1;
  else if (aStr === "-") a = -1;
  else { a = parseNumber(aStr); if (a == null) return null; }

  // p (uwaga na znak z nawiasu)
  const signP = m[2] === "+" ? +1 : -1;
  const pNum = parseNumber(m[3]);
  if (pNum == null) return null;
  const p = signP === +1 ? -pNum : +pNum;

  // q
  const signQ = m[4] === "+" ? +1 : -1;
  const qNum = parseNumber(m[5]);
  if (qNum == null) return null;
  const q = signQ * qNum;

  return { a, p, q };
}

/* ---------- PODGLĄD (bez naruszania zapisu użytkownika) ---------- */

function toLatexFractionSafe(str) {
  if (!str) return "";
  let s = str.replace(/([+\-]?)\s*(\d+)\s+(\d+)\s*\/\s*(\d+)/g,
    (_, sign, whole, num, den) => `${sign}${whole}\\dfrac{${num}}{${den}}`
  );
  s = s.replace(/(?<!\\dfrac\{)(\d+)\/(\d+)/g, (_, num, den) => `\\dfrac{${num}}{${den}}`);
  return s;
}

/* ---------- fallback: numeryczna weryfikacja równoważności ---------- */






/* ---------- generator ---------- */

export function generateTask() {
  const a = randInt(-5, 5, new Set([0]));
  const b = randInt(-9, 9);
  const c = randInt(-9, 9);

  const delta = b*b - 4*a*c;
  const pFrac = simplifyFrac(-b, 2*a);
  const qFrac = simplifyFrac(-delta, 4*a);

  return {
    type: "TransformGeneralToCanonicalTask",
    data: {
      a, b, c, delta,
      pFrac, qFrac,
      p: pFrac.n / pFrac.d,
      q: qFrac.n / qFrac.d,
    }
  };
}

/* ---------- render ---------- */

export function renderTask({ data }) {
  const { a, b, c } = data;
  const body = (() => {
    const term = (coeff, base, omitOne=false) => {
      if (coeff === 0) return "";
      const sign = coeff > 0 ? " + " : " - ";
      const abs = Math.abs(coeff);
      const num = (abs === 1 && omitOne) ? "" : String(abs);
      return `${sign}${num}${base}`;
    };
    const aCoeff = Math.abs(a) === 1 ? (a === -1 ? "-" : "") : String(a);
    let out = `${aCoeff}x^2${term(b,"x",true)}`;
    if (c !== 0) out += (c > 0 ? " + " : " - ") + String(Math.abs(c));
    return out;
  })();

  return (
    <div style={{ marginTop: 6, marginBottom: 6 }}>
      <div>
        Dana jest funkcja w postaci ogólnej: <InlineMath math={'f(x) = ' + body} />
      </div>
      <div>
        Zapisz ją w postaci kanonicznej <InlineMath math={'f(x)=a(x-p)^2+q'} />.
      </div>
    </div>
  );
}

// INPUT: f(x)= u góry; podgląd dopiero po wpisaniu; podgląd = surowy tekst → LaTeX (bez „poprawek”)
export function renderInput(_task, userAnswer, onChange, disabled) {
  const raw = getRaw(userAnswer);
  const hasText = raw.trim().length > 0;

  return (
    <div style={{ display: "grid", gap: 8, marginTop: 6 }}>
      <div><InlineMath math={'f(x) ='} /></div>
      <EquationInput
        value={raw}
        onChange={(val) => onChange(val)}
        disabled={disabled}
        placeholder="np. -3(x-5/6)^2+6 1/12"
        showPreview={false}
      />
      {hasText && (
        <div style={{ opacity: 0.85 }}>
          <span style={{ marginRight: 6 }}>Podgląd:</span>
          <InlineMath math={`f(x) = ${toLatexFractionSafe(raw)}`} />
        </div>
      )}
    </div>
  );
}

/* ---------- check + answers ---------- */

export function checkAnswer(arg1, arg2) {
  const task = arg1 && arg1.data ? arg1 : arg2;
  const userAnswer = arg1 && arg1.data ? arg2 : arg1;
  return checkQuadraticExpression(getRaw(userAnswer), { a: task.data.a, b: -2 * task.data.a * task.data.p, c: task.data.a * task.data.p ** 2 + task.data.q }, { form: "canonical" });
}

// Twoja odpowiedź = to, CO wpisałeś (ładnie z ułamkami)
export function renderUserAnswer(arg1, arg2) {
  const looksLikeTask = (x) => x && typeof x === "object" && x.data;
  const userAnswer = looksLikeTask(arg1) ? arg2 : arg1;

  const raw = getRaw(userAnswer);
  if (!raw || !raw.trim()) return <i>(brak odpowiedzi)</i>;

  return (
    <div style={{ marginTop: 6, textAlign: "left" }}>
      <InlineMath math={`f(x) = ${toLatexFractionSafe(raw)}`} />
    </div>
  );
}

// Oficjalna odpowiedź
export function renderAnswer({ data }) {
  const { a, pFrac, qFrac } = data;
  const fx = canonicalLatex(a, pFrac, qFrac);
  return (
    <div style={{ marginTop: 6, textAlign: "left" }}>
      <InlineMath math={`f(x) = ${fx}`} />
    </div>
  );
}

/* ---------- explanation ---------- */

export function renderExplanation({ data }) {
  const { a, b, c, delta, pFrac, qFrac } = data;
  const b2 = b * b;
  const fourAC = 4 * a * c;

  const pLatex = formatFractionMixed(pFrac.n, pFrac.d);
  const qLatex = formatFractionMixed(qFrac.n, qFrac.d);
  const fxCanonical = canonicalLatex(a, pFrac, qFrac);

  return (
    <div style={{ textAlign: "left", lineHeight: 1.7, marginTop: 8 }}>
      <div><strong>Krok 1.</strong> Wzór kanoniczny i co podstawiamy:</div>
      <BlockMath math={'f(x) = a(x - p)^2 + q'} />
      <BlockMath math={`a = ${a},\\quad p = -\\dfrac{${par(b)}}{2\\cdot${par(a)}},\\quad q = -\\dfrac{\\Delta}{4a},\\quad \\Delta = b^2 - 4ac`} />

      <div><strong>Krok 2.</strong> Obliczamy p i q:</div>
      <BlockMath math={`p = -\\dfrac{${par(b)}}{2\\cdot${par(a)}} = ${pLatex}`} />
      <BlockMath math={`\\Delta = ${par(b)}^{2} - 4\\cdot${par(a)}\\cdot${par(c)} = ${b2} - ${fourAC < 0 ? `(${fourAC})` : `${fourAC}`} = ${delta}`} />
      <BlockMath math={`q = -\\dfrac{${delta}}{4\\cdot${par(a)}} = ${qLatex}`} />

      <div><strong>Krok 3.</strong> Postać kanoniczna:</div>
      <BlockMath math={`f(x) = ${fxCanonical}`} />
    </div>
  );
}

export default {
  generateTask,
  renderTask,
  renderInput,
  checkAnswer,
  renderUserAnswer,
  renderAnswer,
  renderExplanation,
};
